import * as React from 'react';
import Text from 'components/commons/Text';
import { Row } from 'saladsoft-primitive';
import { InnerSection } from 'components/layouts/CommonForm';
import { InnerGroup } from 'components/layouts/CommonForm';
import { Button } from 'saladsoft-primitive';
import { useParams } from 'react-router-dom';
import { useStoreWrapper } from 'stores/StoreHelper';
import { useEffect, useState } from 'react';
import ServiceHelper from 'stores/ServiceHelper';
import { useMultiSelect } from 'components/multiSelectContainer/useMultiSelect';
import ItemList from 'components/multiSelectContainer/ItemList';
import ItemBox from 'components/multiSelectContainer/ItemBox';
import { ItemType } from 'components/multiSelectContainer/types';
import { toast } from 'react-toastify';
import { useStateContext } from 'providers/usePageState';

const LandmarkContainer = () => {
  const { listingId } = useParams();
  const { broadcast } = useStateContext();
  const { uiStore } = useStoreWrapper();

  const [landmarks, setLandmarks] = useState<ItemType[]>([]);

  const {
    selectedItems,
    handleRemoveItems,
    handleSelectItem,
    handleClickAll,
    covertModelToItem,
    unmount,
  } = useMultiSelect<ItemType & { id: number }>(
    landmarks.filter((l): l is ItemType & { id: number } => l.id !== undefined),
  );

  const handleAddLandmarks = async () => {
    const filteredLandmarks = await fetchLandmarks();
    uiStore?.modal.show({
      title: '주변명소 추가하기',
      style: { width: '600px', maxHeight: '80vh', height: '100%' },
      children: (
        <ItemList
          items={filteredLandmarks}
          onSaveItems={(items) => {
            const mergedItems = [...items, ...landmarks];
            handleSave(mergedItems.map((o) => o.id ?? 0));
          }}
        />
      ),
      onConfirmed: () => {
        fetchListingLandmarks();
      },
    });
  };

  const handleSave = (items: number[]) => {
    ServiceHelper?.adminListingService
      ?.saveLandmarks(Number(listingId), items)
      .then((res) => {
        if (res.status < 300) {
          uiStore?.modal.confirm();
          toast.success('선택된 항목을 저장하였습니다.');
          fetchListingLandmarks();
          unmount();
        } else {
          toast.error('선택된 항목 저장에 실패하였습니다.');
        }
      })
      .finally(() => {
        broadcast({ id: 'reload', param: 'landmark' });
      });
  };

  /**
   * @description 시설에 포함된 주변 명소 취득
   */
  const fetchListingLandmarks = () => {
    ServiceHelper.adminListingService?.getLandmarksByListing1(Number(listingId)).then((res) => {
      const items = res.data.map((landmark) => covertModelToItem(landmark));
      setLandmarks(items);
    });
  };

  /**
   * @description 전체 명소 취득
   */
  const fetchLandmarks = async () => {
    const result = await ServiceHelper.adminLandmarkService?.getLandmarks();
    const filteredLandmarks = result?.data
      .map((landmark) => covertModelToItem(landmark))
      .filter((landmark) => !landmarks?.some((checked) => checked.id === landmark.id));
    return filteredLandmarks as ItemType[];
  };

  useEffect(() => {
    if (listingId) {
      fetchListingLandmarks();
    }
  }, [listingId]);

  return (
    <div>
      <InnerSection>
        <InnerGroup style={{ minHeight: 'unset' }}>
          <div>
            <Text type="H3" style={{ marginBottom: 10 }}>
              주변명소 설정
            </Text>
            <Text type="H4" buttonRole="DESCRIPTION" style={{ marginBottom: 10 }}>
              하단의 추가하기 버튼을 클릭하여 주변명소를 지정 해 주세요.
            </Text>
            <Button type="outlined" style={{ width: 150 }} onClick={() => handleAddLandmarks()}>
              추가하기
            </Button>
          </div>
          <div style={{ width: '100%', flex: 1 }}>
            <>
              {landmarks.length > 0 && (
                <Row marginBottom="20px" justifyContent="flex-start" gap="10px">
                  <Button type="outlined" style={{ width: 150 }} onClick={() => handleClickAll()}>
                    {selectedItems.length === landmarks.length ? '전체 선택해제' : '전체선택'}
                  </Button>
                  <>
                    {selectedItems && selectedItems.length > 0 && (
                      <Button
                        type="outlined"
                        style={{ width: 150 }}
                        onClick={() => {
                          const ids = handleRemoveItems();
                          uiStore?.confirm.show({
                            title: '정말 제거하시겠습니까?',
                            onConfirmed: () => {
                              handleSave(ids);
                            },
                          });
                        }}
                      >
                        선택항목 제거하기
                      </Button>
                    )}
                  </>
                </Row>
              )}
            </>
            <Row flexWrap="wrap" width="100%" justifyContent="flex-start" gap="10px">
              {landmarks?.map((landmark, index) => (
                <ItemBox
                  key={landmark.id}
                  item={landmark}
                  onSelect={(item, checked) => {
                    handleSelectItem(item as ItemType & { id: number }, checked);
                  }}
                  checked={selectedItems.some((selected) => selected.id === landmark.id)}
                />
              ))}
            </Row>
          </div>
        </InnerGroup>
      </InnerSection>
    </div>
  );
};

export default LandmarkContainer;
